import { readRowMediaText, readRowTextList } from "./drawer-media-text";

/**
 * Parses CreativeHashData BQ rows into narrative buckets.
 *
 * Port of the Retool transformer used with CreativeHashData (columnar → row-based).
 * Drift tracking: user-provided Retool JS transformer for CreativeHashData.
 */

const SEQUENCE_SLOT_COUNT = 6;

export interface SequenceItem {
  /** Display label from sequence order (e.g. "Hook"). */
  label: string;
  /** Raw time range from sequence time (e.g. "4-10") for tooltip + video seek. */
  timeRange: string | null;
}

export interface ParsedCreativeHashData {
  concepts: string[];
  target_audience: string[];
  personalities: string[];
  topics: string[];
  Age: string[];
  angle: string[];
  painPoint: string[];
  motivation: string[];
  sequence: SequenceItem[];
  verbalHooks: string | null;
  textOverlay: string | null;
  rank: number | null;
}

function toTitle(value: unknown): string {
  return String(value ?? "").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Capitalize only the first character; leave the rest of the string unchanged. */
function capitalizeFirst(value: unknown): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function parseSequence(
  title: string,
  kind: "time" | "order",
): { idx: number; value: string } | null {
  const re = new RegExp(`^sequence\\s+${kind}\\s*(\\d+)\\s*;\\s*(.+)$`, "i");
  const match = String(title ?? "").trim().match(re);
  if (!match) return null;
  return { idx: Number(match[1]) - 1, value: match[2].trim() };
}

function parseTargetAudienceValue(title: string): string {
  const trimmed = String(title ?? "").trim();
  if (trimmed.toLowerCase().startsWith("target audience;")) {
    return capitalizeFirst(trimmed.replace(/^target audience;/i, "").trim());
  }
  return capitalizeFirst(trimmed);
}

function isConceptTargetAudienceRow(row: Record<string, unknown>): boolean {
  return (
    String(row.Scenario ?? "").trim().toLowerCase() === "concept" &&
    String(row.Sub_Feature ?? "").trim().toLowerCase() === "target audience"
  );
}

function isConceptPersonalitiesRow(row: Record<string, unknown>): boolean {
  return (
    String(row.Scenario ?? "").trim().toLowerCase() === "concept" &&
    String(row.Sub_Feature ?? "").trim().toLowerCase() === "personalities"
  );
}

function readTargetAudienceValue(row: Record<string, unknown>): string {
  const title = String(row.Topic_Title ?? "").trim();
  const description = String(row.Topic_Description ?? "").trim();
  return parseTargetAudienceValue(title || description);
}

function readPersonalitiesValue(row: Record<string, unknown>): string {
  const title = String(row.Topic_Title ?? "").trim();
  const description = String(row.Topic_Description ?? "").trim();
  return capitalizeFirst(title || description);
}

type NarrativeField = "painPoint" | "motivation";

function narrativeFieldFromSubFeature(subFeature: unknown): NarrativeField | null {
  switch (String(subFeature ?? "").trim().toLowerCase()) {
    case "painpoint":
      return "painPoint";
    case "motivation":
      return "motivation";
    default:
      return null;
  }
}

function isNarrativeRow(row: Record<string, unknown>): boolean {
  return String(row.Scenario ?? "").trim().toLowerCase() === "narrative";
}

function readNarrativeText(row: Record<string, unknown>): string {
  const title = String(row.Topic_Title ?? "").trim();
  const description = String(row.Topic_Description ?? "").trim();
  return capitalizeFirst(title || description);
}

function uniqueOrdered(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = value.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function readRank(rows: Array<Record<string, unknown>>): number | null {
  const raw = rows[0]?.position;
  let rank: number | null = null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    rank = raw;
  } else {
    const parsed = Number(raw);
    rank = Number.isFinite(parsed) ? parsed : null;
  }
  return rank === -1 ? null : rank;
}

function buildSequenceItems(
  timeSlots: (string | null)[],
  orderSlots: (string | null)[],
): SequenceItem[] {
  const items: SequenceItem[] = [];
  for (let i = 0; i < SEQUENCE_SLOT_COUNT; i++) {
    const label = orderSlots[i];
    const timeRange = timeSlots[i];
    if (!label && !timeRange) continue;
    items.push({
      label: label ?? timeRange ?? "",
      timeRange,
    });
  }
  return items;
}

type ParsedRowAccumulator = Pick<
  ParsedCreativeHashData,
  | "concepts"
  | "target_audience"
  | "personalities"
  | "topics"
  | "Age"
  | "painPoint"
  | "motivation"
>;

function createRowAccumulator(): ParsedRowAccumulator {
  return {
    concepts: [],
    target_audience: [],
    personalities: [],
    topics: [],
    Age: [],
    painPoint: [],
    motivation: [],
  };
}

/** Reduce CreativeHashData rows into concepts, audience, sequence slots, etc. */
export function parseCreativeHashDataRows(
  rows: Array<Record<string, unknown>>,
): ParsedCreativeHashData {
  const sequenceTimeSlots: (string | null)[] = Array(SEQUENCE_SLOT_COUNT).fill(null);
  const sequenceOrderSlots: (string | null)[] = Array(SEQUENCE_SLOT_COUNT).fill(null);

  const result = rows.reduce<ParsedRowAccumulator>(
    (acc, row) => {
      const title = String(row.Topic_Title ?? "").trim();
      const subFeature = row.Sub_Feature;

      if (title.toLowerCase().startsWith("main;")) {
        acc.concepts.push(toTitle(title.replace(/^main;/i, "").trim()));
        return acc;
      }

      if (title.toLowerCase().startsWith("target audience;")) {
        acc.target_audience.push(parseTargetAudienceValue(title));
        return acc;
      }

      if (isConceptTargetAudienceRow(row)) {
        const audience = readTargetAudienceValue(row);
        if (audience) acc.target_audience.push(audience);
        return acc;
      }

      if (isConceptPersonalitiesRow(row)) {
        const personality = readPersonalitiesValue(row);
        if (personality) acc.personalities.push(personality);
        return acc;
      }

      if (isNarrativeRow(row)) {
        const narrativeField = narrativeFieldFromSubFeature(subFeature);
        if (narrativeField) {
          const text = readNarrativeText(row);
          if (text) acc[narrativeField].push(text);
        }
        return acc;
      }

      if (title.toLowerCase().startsWith("age;")) {
        acc.Age.push(toTitle(title.replace(/^age;/i, "").trim()));
        return acc;
      }

      const seqTime = parseSequence(title, "time");
      if (
        seqTime &&
        Number.isFinite(seqTime.idx) &&
        seqTime.idx >= 0 &&
        seqTime.idx < SEQUENCE_SLOT_COUNT
      ) {
        sequenceTimeSlots[seqTime.idx] = seqTime.value;
        return acc;
      }

      const seqOrder = parseSequence(title, "order");
      if (
        seqOrder &&
        Number.isFinite(seqOrder.idx) &&
        seqOrder.idx >= 0 &&
        seqOrder.idx < SEQUENCE_SLOT_COUNT
      ) {
        sequenceOrderSlots[seqOrder.idx] = toTitle(seqOrder.value);
        return acc;
      }

      if (String(subFeature ?? "").trim().toLowerCase() === "automatic") {
        acc.topics.push(toTitle(title));
      }

      return acc;
    },
    createRowAccumulator(),
  );

  return {
    concepts: uniqueOrdered(result.concepts),
    target_audience: uniqueOrdered(result.target_audience),
    personalities: uniqueOrdered(result.personalities),
    topics: uniqueOrdered(result.topics),
    Age: uniqueOrdered(result.Age),
    angle: uniqueOrdered(readRowTextList(rows, "angles").map((value) => capitalizeFirst(value))),
    painPoint: uniqueOrdered(result.painPoint),
    motivation: uniqueOrdered(result.motivation),
    verbalHooks: (() => {
      const value = readRowMediaText(rows, "verbal_hooks");
      return value ? capitalizeFirst(value) : null;
    })(),
    textOverlay: (() => {
      const value = readRowMediaText(rows, "text_overlay");
      return value ? capitalizeFirst(value) : null;
    })(),
    sequence: buildSequenceItems(sequenceTimeSlots, sequenceOrderSlots),
    rank: readRank(rows),
  };
}
